/**
 * Business Partner API
 * @description Partner (Merchant) management
 */
import { request } from '@umijs/max';

const BASE_PATH = '/api/partners';

// ============ Partners ============

/** Get current user's partners */
export async function getPartners(options?: { [key: string]: any }) {
  return request<BusinessAPI.ListResponse<BusinessAPI.Partner>>(
    `${BASE_PATH}/my-partners`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** Get partner by ID */
export async function getPartner(
  id: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.Partner> {
  const result = await request<any>(
    `${BASE_PATH}/${id}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  // Handle response format: { success, data } -> return data
  if (result?.data) {
    return result.data;
  }
  return result;
}

/** Sync partner data from Adyen and return fresh PartnerDto */
export async function syncAdyenData(
  id: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.Partner> {
  const result = await request<any>(
    `${BASE_PATH}/${id}/adyen/sync`,
    { method: 'POST', ...(options || {}) },
  );
  if (result?.data) return result.data;
  return result;
}

/** Create partner */
export async function createPartner(
  data: BusinessAPI.CreatePartnerRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.Partner>>(BASE_PATH, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

/** Update partner */
export async function updatePartner(
  id: number,
  data: BusinessAPI.UpdatePartnerRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.Partner>>(
    `${BASE_PATH}/${id}`,
    {
      method: 'PUT',
      data,
      ...(options || {}),
    },
  );
}

/** Delete partner (soft delete) */
export async function deletePartner(
  id: number,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<void>>(`${BASE_PATH}/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// ============ Balance Accounts ============

/** Get balance accounts for a partner */
export async function getPartnerBalanceAccounts(
  partnerId: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.BalanceAccount[]> {
  const result = await request<any>(
    `${BASE_PATH}/${partnerId}/balance-accounts`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  // Handle response format: { success, data: [...] }
  // UmiJS request returns axios response, so actual data is in result.data.data
  if (result?.data?.data && Array.isArray(result.data.data)) {
    return result.data.data;
  }
  if (Array.isArray(result)) {
    return result;
  }
  if (result?.data && Array.isArray(result.data)) {
    return result.data;
  }
  if (result?.data?.list && Array.isArray(result.data.list)) {
    return result.data.list;
  }
  return [];
}

/** Sync balance account balance from Adyen */
export async function syncBalanceAccount(
  balanceAccountId: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.BalanceAccount> {
  const result = await request<any>(
    `${BASE_PATH}/balance-accounts/${balanceAccountId}/sync-balance`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  if (result?.data) return result.data;
  return result;
}

/** Create balance account for a partner */
export async function createPartnerBalanceAccount(
  partnerId: number,
  data: BusinessAPI.CreateBalanceAccountRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.BalanceAccount>>(
    `${BASE_PATH}/${partnerId}/balance-accounts`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

/** Update balance account */
export async function updatePartnerBalanceAccount(
  balanceAccountId: number,
  data: BusinessAPI.UpdateBalanceAccountRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.BalanceAccount>>(
    `${BASE_PATH}/balance-accounts/${balanceAccountId}`,
    {
      method: 'PATCH',
      data,
      ...(options || {}),
    },
  );
}

// ============ Session Token ============

/** Create session token for Platform Experience components */
export async function createPartnerSessionToken(
  partnerId: number,
  options?: { [key: string]: any },
) {
  return request<
    BusinessAPI.ApiResponse<BusinessAPI.SessionTokenResponse>
  >(`${BASE_PATH}/${partnerId}/session-token`, {
    method: 'POST',
    data: {
      allowOrigin: window.location.origin,
    },
    ...(options || {}),
  });
}

// ============ Onboarding ============

/** Get hosted onboarding link */
export async function getOnboardingLink(
  partnerId: number,
  returnUrl: string,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.OnboardingLinkResponse>>(
    `${BASE_PATH}/${partnerId}/adyen/onboarding-link`,
    {
      method: 'GET',
      params: { returnUrl },
      ...(options || {}),
    },
  );
}

/** Initiate Adyen onboarding (setup business line and payment methods) */
export async function initiateAdyenOnboarding(
  partnerId: number,
  data?: BusinessAPI.InitiateOnboardingRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.Partner>>(
    `${BASE_PATH}/${partnerId}/adyen/initiate`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

// ============ Business Lines ============

/** Get business lines for a partner */
export async function getBusinessLines(
  partnerId: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.BusinessLine[]> {
  const result = await request<any>(
    `${BASE_PATH}/${partnerId}/business-lines`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  // Handle response format: { success, data: { businessLines: [...] } }
  if (result?.data?.businessLines && Array.isArray(result.data.businessLines)) {
    return result.data.businessLines;
  }
  if (Array.isArray(result)) {
    return result;
  }
  return [];
}

/** Create a business line for a partner */
export const createBusinessLine = (
  partnerId: number,
  data: BusinessAPI.CreateBusinessLineRequest,
) =>
  request<BusinessAPI.BusinessLine>(
    `${BASE_PATH}/${partnerId}/business-lines`,
    { method: 'POST', data },
  );

/** Update a business line */
export const updateBusinessLine = (
  partnerId: number,
  businessLineId: string,
  data: BusinessAPI.UpdateBusinessLineRequest,
) =>
  request<BusinessAPI.BusinessLine>(
    `${BASE_PATH}/${partnerId}/business-lines/${businessLineId}`,
    { method: 'PATCH', data },
  );

/** Delete a business line */
export const deleteBusinessLine = (
  partnerId: number,
  businessLineId: string,
) =>
  request(`${BASE_PATH}/${partnerId}/business-lines/${businessLineId}`, {
    method: 'DELETE',
  });

// ============ Payment Methods ============

/** Get payment methods (optionally filtered by businessLineId) */
export async function getPaymentMethods(
  businessLineId?: string,
  options?: { [key: string]: any },
): Promise<BusinessAPI.PaymentMethod[]> {
  const url = businessLineId
    ? `/api/payment-methods?businessLineId=${businessLineId}`
    : '/api/payment-methods';
  const result = await request<any>(url, {
    method: 'GET',
    ...(options || {}),
  });
  // Handle response format: { success, data: { data: [...], itemsTotal, pagesTotal } }
  if (result?.data?.data && Array.isArray(result.data.data)) {
    return result.data.data;
  }
  if (result?.data && Array.isArray(result.data)) {
    return result.data;
  }
  if (Array.isArray(result)) {
    return result;
  }
  return [];
}

// ============ Transfer Instruments (Bank Accounts) ============

/** List transfer instruments for a partner */
export async function getTransferInstruments(
  partnerId: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.TransferInstrument[]> {
  const result = await request<any>(
    `${BASE_PATH}/${partnerId}/transfer-instruments`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  if (result?.data && Array.isArray(result.data)) {
    return result.data;
  }
  if (Array.isArray(result)) {
    return result;
  }
  return [];
}

/** Create a transfer instrument (bank account) for a partner */
export async function createTransferInstrument(
  partnerId: number,
  data: BusinessAPI.CreateTransferInstrumentRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.TransferInstrument>>(
    `${BASE_PATH}/${partnerId}/transfer-instruments`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

/** Delete a transfer instrument */
export async function deleteTransferInstrument(
  partnerId: number,
  transferInstrumentId: string,
  options?: { [key: string]: any },
) {
  return request(
    `${BASE_PATH}/${partnerId}/transfer-instruments/${transferInstrumentId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

/** Get transfer instrument detail */
export async function getTransferInstrument(
  partnerId: number,
  transferInstrumentId: string,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.TransferInstrumentDetail>>(
    `${BASE_PATH}/${partnerId}/transfer-instruments/${transferInstrumentId}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

// ============ Sweeps (Automatic Payouts) ============

/** List sweeps for a balance account */
export async function getSweeps(
  balanceAccountId: number,
  options?: { [key: string]: any },
): Promise<BusinessAPI.Sweep[]> {
  const result = await request<any>(
    `${BASE_PATH}/balance-accounts/${balanceAccountId}/sweeps`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  if (result?.data && Array.isArray(result.data)) {
    return result.data;
  }
  if (Array.isArray(result)) {
    return result;
  }
  return [];
}

/** Create a sweep for a balance account */
export async function createSweep(
  balanceAccountId: number,
  data: BusinessAPI.CreateSweepRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.Sweep>>(
    `${BASE_PATH}/balance-accounts/${balanceAccountId}/sweeps`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

/** Update a sweep */
export async function updateSweep(
  balanceAccountId: number,
  sweepId: string,
  data: BusinessAPI.UpdateSweepRequest,
  options?: { [key: string]: any },
) {
  return request<BusinessAPI.ApiResponse<BusinessAPI.Sweep>>(
    `${BASE_PATH}/balance-accounts/${balanceAccountId}/sweeps/${sweepId}`,
    {
      method: 'PATCH',
      data,
      ...(options || {}),
    },
  );
}

/** Delete a sweep */
export async function deleteSweep(
  balanceAccountId: number,
  sweepId: string,
  options?: { [key: string]: any },
) {
  return request(
    `${BASE_PATH}/balance-accounts/${balanceAccountId}/sweeps/${sweepId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

/** Create a payment method */
export const createPaymentMethod = (
  data: BusinessAPI.CreatePaymentMethodRequest,
) =>
  request<BusinessAPI.PaymentMethod>(`/api/payment-methods`, {
    method: 'POST',
    data,
  });

/** Update a payment method */
export const updatePaymentMethod = (
  paymentMethodId: string,
  data: BusinessAPI.UpdatePaymentMethodRequest,
) =>
  request<BusinessAPI.PaymentMethod>(
    `/api/payment-methods/${paymentMethodId}`,
    { method: 'PATCH', data },
  );

/** Disable a payment method */
export const disablePaymentMethod = (paymentMethodId: string) =>
  request(`/api/payment-methods/${paymentMethodId}`, {
    method: 'PATCH',
    data: { enabled: false },
  });

/**
 * Get the Apple Pay domains currently registered on a merchant-level Apple Pay PM.
 */
export async function getApplePayDomains(
  paymentMethodId: string,
  options?: { [key: string]: any },
): Promise<BusinessAPI.ApplePayInfo> {
  const result = await request<any>(
    `/api/payment-methods/${paymentMethodId}/apple-pay-domains`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  if (result?.data?.data) return result.data.data;
  if (result?.data) return result.data;
  return result;
}

/**
 * Add Apple Pay domains to a merchant-level Apple Pay PM (append semantics — cannot remove).
 */
export const addApplePayDomains = (
  paymentMethodId: string,
  data: BusinessAPI.ApplePayInfo,
) =>
  request<void>(`/api/payment-methods/${paymentMethodId}/apple-pay-domains`, {
    method: 'POST',
    data,
  });
